import { useState, useCallback } from 'react';

interface PreloadedImages {
  [key: string]: boolean;
}

export const useImagePreloader = () => {
  const [loadedImages, setLoadedImages] = useState<PreloadedImages>({});
  const [isPreloading, setIsPreloading] = useState(false);

  const preloadImage = useCallback((src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // loadedImagesの現在の値を直接参照せず、setLoadedImagesのコールバック内で確認
      setLoadedImages(prev => {
        if (prev[src]) {
          resolve();
          return prev;
        }
        
        const img = new Image();
        img.onload = () => {
          setLoadedImages(current => ({ ...current, [src]: true }));
          resolve();
        };
        img.onerror = () => {
          console.error(`Failed to preload image: ${src}`);
          reject(new Error(`Failed to load ${src}`));
        };
        img.src = src;
        
        return prev;
      });
    });
  }, []);

  const preloadImages = useCallback(async (imageUrls: string[]) => {
    if (imageUrls.length === 0) return;
    
    setIsPreloading(true);
    const validUrls = imageUrls.filter(url => url && url.length > 0);
    
    try {
      await Promise.allSettled(
        validUrls.map(url => preloadImage(url))
      );
    } catch (error) {
      console.error('Error preloading images:', error);
    } finally {
      setIsPreloading(false);
    }
  }, [preloadImage]);

  const preloadProjectImages = useCallback(async (projects: any[]) => {
    const thumbnailUrls: string[] = [];
    
    projects.forEach(project => {
      if (project.images && project.images.length > 0) {
        thumbnailUrls.push(project.images[0]);
      }
      if (project.thumbnail) {
        thumbnailUrls.push(project.thumbnail);
      }
    });

    await preloadImages(thumbnailUrls);
  }, [preloadImages]);

  const isImageLoaded = useCallback((src: string): boolean => {
    return loadedImages[src] || false;
  }, [loadedImages]);

  return {
    preloadImage,
    preloadImages,
    preloadProjectImages,
    isImageLoaded,
    isPreloading,
    loadedImages
  };
};